import usePrivateAxios from "@/common/api/usePrivateAxios";
import { defaultTransformerResponse } from "@/common/constants/defaultTransformerResponse.constant";
import { Mapper } from "@/common/services/mapper";
import { MetaResponse } from "@/common/types/metaResponse";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

type UseEstablishmentCompaniesProps<T> = {
  filters?: {
    search?: string;
    page?: number;
    productCategoryId?: string;
    status?: string;
    stateId?: string;
    cityId?: string;
    commercialKind?: string;
    lastConsumptionCreatedStartDate?: string;
    lastConsumptionCreatedEndDate?: string;
    caModuleEnabled?: boolean;
    cmModuleEnabled?: boolean;
    tmaisModuleEnabled?: boolean;
  };
  mapper?: (data: any) => T,
  enabled?: boolean;
};

export const useEstablishmentCompanies = <T,>({
  filters: {
    search = '',
    page,
    productCategoryId,
    stateId,
    status,
    cityId,
    commercialKind,
    lastConsumptionCreatedStartDate,
    lastConsumptionCreatedEndDate,
    caModuleEnabled,
    cmModuleEnabled,
    tmaisModuleEnabled,
  } = {},
  mapper = Mapper.default<T>(),
  enabled = true,
}: UseEstablishmentCompaniesProps<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    isPending,
    ...rest
  } = useQuery({
    queryKey: [
      'establishmentCompanies',
      search,
      page,
      productCategoryId,
      cityId,
      status,
      stateId,
      commercialKind,
      lastConsumptionCreatedStartDate,
      lastConsumptionCreatedEndDate,
      caModuleEnabled,
      cmModuleEnabled,
      tmaisModuleEnabled,
    ],
    queryFn: () => {
      return privateAxios.get('/establishment/companies', {
        params: {
          search,
          page,
          'q[product_category_id_eq]': productCategoryId,
          'q[status_eq]': status,
          'q[city_id_eq]': cityId,
          'q[state_id_eq]': stateId,
          'q[commercial_kind_eq]': commercialKind,
          'q[last_consumption_created_at_gteq]': lastConsumptionCreatedStartDate,
          'q[last_consumption_created_at_lteq_or_null]': lastConsumptionCreatedEndDate,
          'q[ca_module_enabled_true]': caModuleEnabled,
          'q[cm_module_enabled_true]': cmModuleEnabled,
          'q[tmais_module_enabled_true]': tmaisModuleEnabled,
        }
      });
    },
    select: (data: { data: { data: any[], meta: MetaResponse; }; }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      };
    },
    placeholderData: keepPreviousData,
    enabled,
  });

  return { data, meta, isPending, ...rest };
};
